"use client";

import { useCallback } from "react";
import { toast } from "sonner";

import { ToastOptions, UseAppNotificationsReturn } from "@/shared/types/toast";

const DEFAULT_DURATION = 4000;

type SonnerOptions = Parameters<typeof toast.success>[1];

const buildToastOptions = (options?: ToastOptions): SonnerOptions => ({
  duration: options?.duration ?? DEFAULT_DURATION,
  description: options?.description,
  action: options?.action,
});

export function useAppNotifications(): UseAppNotificationsReturn {
  const success = useCallback(
    (message: string, options?: ToastOptions): void => {
      toast.success(message, { ...buildToastOptions(options), id: `success:${message}:${options?.description ?? ""}` });
    },
    [],
  );

  const error = useCallback((message: string, options?: ToastOptions): void => {
    toast.error(message, { ...buildToastOptions(options), id: `error:${message}:${options?.description ?? message}` });
  }, []);

  const warning = useCallback(
    (message: string, options?: ToastOptions): void => {
      toast.warning(message, { ...buildToastOptions(options), id: `warning:${message}:${options?.description ?? ""}` });
    },
    [],
  );

  const info = useCallback((message: string, options?: ToastOptions): void => {
    toast.info(message, { ...buildToastOptions(options), id: `info:${message}:${options?.description ?? ""}` });
  }, []);

  const dismiss = useCallback((toastId: string) => {
    toast.dismiss(toastId);
  }, []);

  const dismissAll = useCallback(() => {
    toast.dismiss();
  }, []);

  const promise = useCallback(
    async <T>(
      promise: Promise<T>,
      messages: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((error: unknown) => string);
      },
      options?: ToastOptions,
    ): Promise<T> => {
      toast.promise(promise, {
        loading: messages.loading,
        success: messages.success,
        error: messages.error,
        ...buildToastOptions(options),
      });
      return promise;
    },
    [],
  );

  return {
    success,
    error,
    warning,
    info,
    dismiss,
    dismissAll,
    promise,
  };
}

// Экспорт для обратной совместимости
export { useAppNotifications as useNotifications };
export { useAppNotifications as useToast };
